import {
    PAGE_FRAGMENT_TYPE,
    type CreateExtArgs,
    type CreateExtensionArgument,
    type DeepFunctionMap,
    type DmApis,
    type DocumentDataTypes,
    type Extension,
    type ExtensionAPI,
    type ValidateValue
} from '@wix/document-manager-core'
import type {
    Pointer,
    DependentRef,
    DependencyRef,
    DependsOnItem,
    DependedByItem,
    DalValue
} from '@wix/document-services-types'
import _ from 'lodash'
import {generateItemIdWithPrefix} from '../utils/dataUtils'
import type {FragmentsExtensionAPI} from './fragments/fragments'
import type {DataModelExtensionAPI} from './dataModel/dataModel'

const DEPENDS_ON_NAMESPACE = 'dependsOn'
const DEPENDED_BY_NAMESPACE = 'dependedBy'
const DEFAULT_ON_REMOVE = 'detach' as const
const DEPENDED_BY_SOURCE_INDEX = 'fragmentDependenciesDependedBySource'

const getDependedBySourceIndexKey = (namespace: string, id: string) => JSON.stringify([namespace, id])

const getDependencyPointer = (id: string, namespace: string, fragmentType: string, fragmentInstance: string): Pointer =>
    fragmentType === PAGE_FRAGMENT_TYPE ? {id, type: namespace} : {id, type: namespace, fragmentType, fragmentInstance}

const getCorrespondingDependedByPointer = (item: DependsOnItem): Pointer =>
    getDependencyPointer(item.to.dependedById, DEPENDED_BY_NAMESPACE, item.to.fragmentType, item.to.fragmentInstance)

const getCorrespondingDependsOnPointer = (item: DependedByItem): Pointer =>
    getDependencyPointer(item.to.dependsOnId, DEPENDS_ON_NAMESPACE, item.to.fragmentType, item.to.fragmentInstance)

export interface FragmentDependenciesAPI extends DeepFunctionMap {
    /**
     * Creates a bidirectional dependency between two fragments.
     * Validates that the dependency target namespace and item type
     * are allowed by the provider fragment's dependableItems config.
     * Creates a DependsOnItem in the dependent fragment and a
     * DependedByItem in the dependency fragment, linked via
     * dependedById / dependsOnId.
     */
    addDependency(dependent: DependentRef, dependency: DependencyRef): {dependsOnId: string; dependedById: string}

    /**
     * Removes all dependency links associated with an item.
     * Handles both directions:
     * - If the item has dependsOn entries: removes those entries
     *   and their corresponding dependedBy entries.
     * - If the item has dependedBy entries: removes those entries
     *   and their corresponding dependsOn entries. If the
     *   dependent fragment's dependencyPolicy specifies
     *   onRemove: 'delete' for the namespace, also removes
     *   the dependent item.
     */
    removeDependenciesFromItem(pointer: Pointer): void

    /** Returns all DependsOnItem entries for a given item. */
    getItemDependencies(pointer: Pointer): DependsOnItem[]

    /** Returns all DependedByItem entries for a given item. */
    getItemDependents(pointer: Pointer): DependedByItem[]

    /** Returns all DependsOnItem entries across all instances of a fragment type. */
    getDependsOnItemsByFragment(fragmentType: string): DependsOnItem[]

    /** Returns all DependedByItem entries across all instances of a fragment type. */
    getDependedByItemsByFragment(fragmentType: string): DependedByItem[]

    /** Direct lookup of the corresponding DependedByItem. */
    findCorrespondingDependedByEntry(dependsOnItem: DependsOnItem): DependedByItem | undefined

    /** Direct lookup of the corresponding DependsOnItem. */
    findCorrespondingDependsOnEntry(dependedByItem: DependedByItem): DependsOnItem | undefined
}

export interface FragmentDependenciesExtensionAPI extends ExtensionAPI {
    fragmentDependencies: FragmentDependenciesAPI
}

const getDocumentDataTypes = (): DocumentDataTypes => ({
    [DEPENDS_ON_NAMESPACE]: {hasSignature: true},
    [DEPENDED_BY_NAMESPACE]: {hasSignature: true}
})

const createFilters = () => ({
    [DEPENDED_BY_SOURCE_INDEX]: (namespace: string, value: DalValue): string[] => {
        if (namespace !== DEPENDED_BY_NAMESPACE || !value) {
            return []
        }
        const item = value as DependedByItem
        return [getDependedBySourceIndexKey(item.from.namespace, item.from.id)]
    }
})

const createExtension = ({dsConfig}: CreateExtensionArgument): Extension => {
    const getSupportedFragments = () => dsConfig.supportedFragments ?? {}

    const getFragmentConfig = (fragmentType: string) => getSupportedFragments()[fragmentType]

    const getDependencyPolicy = (fragmentType: string, namespace: string) => {
        const policy = getFragmentConfig(fragmentType)?.dependencyPolicy
        if (!policy) {
            return {onRemove: DEFAULT_ON_REMOVE}
        }
        if (policy[namespace]) {
            const entry = policy[namespace]
            return {...entry, onRemove: entry.onRemove ?? DEFAULT_ON_REMOVE}
        }
        return policy.default ?? {onRemove: DEFAULT_ON_REMOVE}
    }

    const createExtensionAPI = ({dal, extensionAPI}: CreateExtArgs): FragmentDependenciesExtensionAPI => {
        const {fragments} = extensionAPI as FragmentsExtensionAPI
        const {dataModel} = extensionAPI as DataModelExtensionAPI

        const isPageFragment = (fragmentType: string) => fragmentType === PAGE_FRAGMENT_TYPE

        const addToStore = (item: DalValue, namespace: string, fragmentType: string, fragmentInstance: string) => {
            if (isPageFragment(fragmentType)) {
                const {id, ...itemData} = item
                dataModel.addItem(itemData, namespace, fragmentInstance, id)
            } else {
                fragments.add(item, namespace, fragmentType, fragmentInstance)
            }
        }

        const getStorePointer = (
            id: string,
            namespace: string,
            fragmentType: string,
            fragmentInstance: string
        ): Pointer =>
            isPageFragment(fragmentType) ? {id, type: namespace} : {id, type: namespace, fragmentType, fragmentInstance}

        const removeFromStore = (id: string, namespace: string, fragmentType: string, fragmentInstance: string) =>
            dal.remove(getStorePointer(id, namespace, fragmentType, fragmentInstance))

        const getFromStore = (id: string, namespace: string, fragmentType: string, fragmentInstance: string) =>
            dal.get(getStorePointer(id, namespace, fragmentType, fragmentInstance))

        const validateDependableTarget = (dependency: DependencyRef): void => {
            const dependencyFragmentConfig = getFragmentConfig(dependency.fragmentType)
            if (!dependencyFragmentConfig?.dependableItems) {
                return
            }
            const allowedEntry = dependencyFragmentConfig.dependableItems[dependency.namespace]
            if (!allowedEntry) {
                throw new Error(
                    `Namespace "${dependency.namespace}" is not dependable on fragment type "${dependency.fragmentType}"`
                )
            }
            const targetItem = getFromStore(
                dependency.id,
                dependency.namespace,
                dependency.fragmentType,
                dependency.fragmentInstance
            )
            if (targetItem && !allowedEntry.types.includes(targetItem.type)) {
                throw new Error(
                    `Item "${dependency.id}" has type "${targetItem.type}" which is not allowed for namespace "${
                        dependency.namespace
                    }" on fragment type "${dependency.fragmentType}". Allowed types: ${allowedEntry.types.join(', ')}`
                )
            }
        }

        const addDependency = (
            dependent: DependentRef,
            dependency: DependencyRef
        ): {dependsOnId: string; dependedById: string} => {
            validateDependableTarget(dependency)

            const dependsOnId = generateItemIdWithPrefix(DEPENDS_ON_NAMESPACE)
            const dependedById = generateItemIdWithPrefix(DEPENDED_BY_NAMESPACE)

            const dependsOnItem: DependsOnItem = {
                id: dependsOnId,
                type: 'DependsOnItem',
                from: {
                    namespace: dependent.namespace,
                    id: dependent.id,
                    ...(dependent.reference && {reference: dependent.reference})
                },
                to: {
                    fragmentType: dependency.fragmentType,
                    fragmentInstance: dependency.fragmentInstance,
                    dependedById
                }
            }

            const dependedByItem: DependedByItem = {
                id: dependedById,
                type: 'DependedByItem',
                from: {
                    namespace: dependency.namespace,
                    id: dependency.id
                },
                to: {
                    fragmentType: dependent.fragmentType,
                    fragmentInstance: dependent.fragmentInstance,
                    dependsOnId
                }
            }

            addToStore(dependsOnItem, DEPENDS_ON_NAMESPACE, dependent.fragmentType, dependent.fragmentInstance)
            addToStore(dependedByItem, DEPENDED_BY_NAMESPACE, dependency.fragmentType, dependency.fragmentInstance)

            return {dependsOnId, dependedById}
        }

        const getEntriesByFragment = <T>(namespace: string, fragmentType: string): T[] => {
            const itemPointers = dal.getItemsInNamespace(fragmentType, namespace)
            return itemPointers.map(p => dal.get(p) as T).filter(Boolean)
        }

        const getDependsOnItemsByFragment = (fragmentType: string): DependsOnItem[] =>
            getEntriesByFragment<DependsOnItem>(DEPENDS_ON_NAMESPACE, fragmentType)

        const getDependedByItemsByFragment = (fragmentType: string): DependedByItem[] =>
            getEntriesByFragment<DependedByItem>(DEPENDED_BY_NAMESPACE, fragmentType)

        const getItemDependencies = (pointer: Pointer): DependsOnItem[] =>
            getDependsOnItemsByFragment(pointer.fragmentType ?? PAGE_FRAGMENT_TYPE).filter(
                entry => entry.from.id === pointer.id && entry.from.namespace === pointer.type
            )

        const getItemDependents = (pointer: Pointer): DependedByItem[] =>
            getDependedByItemsByFragment(pointer.fragmentType ?? PAGE_FRAGMENT_TYPE).filter(
                entry => entry.from.id === pointer.id && entry.from.namespace === pointer.type
            )

        const findCorrespondingDependedByEntry = (dependsOnItem: DependsOnItem): DependedByItem | undefined =>
            dal.get(getCorrespondingDependedByPointer(dependsOnItem)) as DependedByItem | undefined

        const findCorrespondingDependsOnEntry = (dependedByItem: DependedByItem): DependsOnItem | undefined =>
            dal.get(getCorrespondingDependsOnPointer(dependedByItem)) as DependsOnItem | undefined

        const getMatchingEntries = <T extends DependsOnItem | DependedByItem>(
            pointer: Pointer,
            namespace: string
        ): {dalPointer: Pointer; item: T}[] => {
            const dalPointers = dal.getItemsInNamespace(pointer.fragmentType ?? PAGE_FRAGMENT_TYPE, namespace)
            return dalPointers.flatMap(dalPointer => {
                const item = dal.get(dalPointer) as T
                return item && item.from.id === pointer.id && item.from.namespace === pointer.type
                    ? [{dalPointer, item}]
                    : []
            })
        }

        const removeDependenciesFromItem = (pointer: Pointer): void => {
            const dependsOnEntries = getMatchingEntries<DependsOnItem>(pointer, DEPENDS_ON_NAMESPACE)
            _.forEach(dependsOnEntries, ({dalPointer, item}) => {
                removeFromStore(
                    item.to.dependedById,
                    DEPENDED_BY_NAMESPACE,
                    item.to.fragmentType,
                    item.to.fragmentInstance
                )
                dal.remove(dalPointer)
            })

            const dependedByEntries = getMatchingEntries<DependedByItem>(pointer, DEPENDED_BY_NAMESPACE)
            _.forEach(dependedByEntries, ({dalPointer, item}) => {
                const correspondingDependsOn = getFromStore(
                    item.to.dependsOnId,
                    DEPENDS_ON_NAMESPACE,
                    item.to.fragmentType,
                    item.to.fragmentInstance
                ) as DependsOnItem | undefined

                removeFromStore(
                    item.to.dependsOnId,
                    DEPENDS_ON_NAMESPACE,
                    item.to.fragmentType,
                    item.to.fragmentInstance
                )
                dal.remove(dalPointer)

                if (correspondingDependsOn) {
                    const policy = getDependencyPolicy(item.to.fragmentType, correspondingDependsOn.from.namespace)
                    if (policy.onRemove === 'delete') {
                        const dependentItemPointer = getStorePointer(
                            correspondingDependsOn.from.id,
                            correspondingDependsOn.from.namespace,
                            item.to.fragmentType,
                            item.to.fragmentInstance
                        )
                        removeDependenciesFromItem(dependentItemPointer)
                        dal.remove(dependentItemPointer)
                    }
                }
            })
        }

        return {
            fragmentDependencies: {
                addDependency,
                removeDependenciesFromItem,
                getItemDependencies,
                getItemDependents,
                getDependsOnItemsByFragment,
                getDependedByItemsByFragment,
                findCorrespondingDependedByEntry,
                findCorrespondingDependsOnEntry
            }
        }
    }

    const createValidator = ({dal, coreConfig}: DmApis): Record<string, ValidateValue> => {
        const supportedFragments = coreConfig.supportedFragments ?? {}

        const validateDependsOnBidirectional: ValidateValue = (pointer, value) => {
            if (pointer.type !== DEPENDS_ON_NAMESPACE || !value) {
                return
            }
            const item = value as DependsOnItem
            const correspondingPointer = getCorrespondingDependedByPointer(item)
            const correspondingItem = dal.get(correspondingPointer)
            if (!correspondingItem) {
                return [
                    {
                        shouldFail: true,
                        type: 'missingCorrespondingDependedBy',
                        message: `DependsOnItem "${item.id}" references dependedById "${correspondingPointer.id}" which does not exist`,
                        extras: {id: item.id, dependedById: correspondingPointer.id}
                    }
                ]
            }
        }

        const resolveCorrespondingDependsOn = (item: DependedByItem) => {
            const pointer = getCorrespondingDependsOnPointer(item)
            return {pointer, item: dal.get(pointer) as DependsOnItem | undefined}
        }

        const createMissingCorrespondingDependsOnError = (item: DependedByItem, pointer: Pointer) => ({
            shouldFail: true,
            type: 'missingCorrespondingDependsOn',
            message: `DependedByItem "${item.id}" references dependsOnId "${pointer.id}" which does not exist`,
            extras: {id: item.id, dependsOnId: pointer.id}
        })

        const validateDependedByBidirectional: ValidateValue = (pointer, value) => {
            if (pointer.type !== DEPENDED_BY_NAMESPACE || !value) {
                return
            }
            const item = value as DependedByItem
            const correspondingDependsOn = resolveCorrespondingDependsOn(item)
            if (!correspondingDependsOn.item) {
                return [createMissingCorrespondingDependsOnError(item, correspondingDependsOn.pointer)]
            }
        }

        const validateDependsOnConfig: ValidateValue = (pointer, value) => {
            if (pointer.type !== DEPENDS_ON_NAMESPACE || !value) {
                return
            }
            const item = value as DependsOnItem
            const {fragmentType: targetFragmentType} = item.to
            const providerConfig = supportedFragments[targetFragmentType]
            if (!providerConfig?.dependableItems) {
                return
            }
            const dependedByPointer = getCorrespondingDependedByPointer(item)
            const dependedByItem = dal.get(dependedByPointer) as DependedByItem | undefined
            if (!dependedByItem) {
                return
            }
            const targetNamespace = dependedByItem.from.namespace
            const targetId = dependedByItem.from.id
            const allowedEntry = providerConfig.dependableItems[targetNamespace]
            if (!allowedEntry) {
                return [
                    {
                        shouldFail: true,
                        type: 'dependencyTargetNotAllowed',
                        message: `Namespace "${targetNamespace}" is not dependable on fragment type "${targetFragmentType}"`,
                        extras: {id: item.id, namespace: targetNamespace, targetFragmentType}
                    }
                ]
            }
            const targetItemPointer = getDependencyPointer(
                targetId,
                targetNamespace,
                targetFragmentType,
                item.to.fragmentInstance
            )
            const targetItem = dal.get(targetItemPointer)
            if (targetItem && !allowedEntry.types.includes(targetItem.type)) {
                return [
                    {
                        shouldFail: true,
                        type: 'dependencyTargetTypeNotAllowed',
                        message: `Item "${targetId}" has type "${
                            targetItem.type
                        }" which is not allowed for namespace "${targetNamespace}" on fragment type "${targetFragmentType}". Allowed types: ${allowedEntry.types.join(
                            ', '
                        )}`,
                        extras: {id: item.id, targetId, type: targetItem.type, allowedTypes: allowedEntry.types}
                    }
                ]
            }
        }

        const resolveSourceFragment = (pointer: Pointer, validatorFragmentType: string) => {
            const fragmentType = pointer.fragmentType ?? validatorFragmentType
            const fragmentInstance = pointer.fragmentInstance ?? supportedFragments[fragmentType]?.singletonInstance
            const isPage = fragmentType === PAGE_FRAGMENT_TYPE
            return {fragmentType, fragmentInstance, isPage}
        }

        const validateDependsOnReferenceExists: ValidateValue = (pointer, value, fragmentType) => {
            if (pointer.type !== DEPENDS_ON_NAMESPACE || !value) {
                return
            }
            const item = value as DependsOnItem
            const {namespace, id} = item.from
            const source = resolveSourceFragment(pointer, fragmentType)
            const refPointer: Pointer = source.isPage
                ? {id, type: namespace}
                : {id, type: namespace, fragmentType: source.fragmentType, fragmentInstance: source.fragmentInstance}
            const referencedItem = dal.get(refPointer)
            if (!referencedItem) {
                return [
                    {
                        shouldFail: true,
                        type: 'dependsOnReferenceNotFound',
                        message: `DependsOnItem "${item.id}" references item "${id}" in namespace "${namespace}" which does not exist`,
                        extras: {id: item.id, referencedId: id, namespace}
                    }
                ]
            }
        }

        const validateDependedByReferenceExists: ValidateValue = (pointer, value, fragmentType) => {
            if (pointer.type !== DEPENDED_BY_NAMESPACE || !value) {
                return
            }
            const item = value as DependedByItem
            const {namespace, id} = item.from
            const source = resolveSourceFragment(pointer, fragmentType)
            const refPointer: Pointer = source.isPage
                ? {id, type: namespace}
                : {id, type: namespace, fragmentType: source.fragmentType, fragmentInstance: source.fragmentInstance}
            const referencedItem = dal.get(refPointer)
            if (!referencedItem) {
                return [
                    {
                        shouldFail: true,
                        type: 'dependedByReferenceNotFound',
                        message: `DependedByItem "${item.id}" references item "${id}" in namespace "${namespace}" which does not exist`,
                        extras: {id: item.id, referencedId: id, namespace}
                    }
                ]
            }
        }

        const validateNoDependentsOnDelete: ValidateValue = (pointer, value, validatorFragmentType) => {
            if (!_.isNil(value)) {
                return
            }
            const fragmentType = pointer.fragmentType ?? validatorFragmentType ?? PAGE_FRAGMENT_TYPE
            const indexKey = dal.queryFilterGetters[DEPENDED_BY_SOURCE_INDEX](
                getDependedBySourceIndexKey(pointer.type, pointer.id)
            )
            const dependents = _.values(
                dal.query(DEPENDED_BY_NAMESPACE, indexKey, undefined, fragmentType)
            ) as DependedByItem[]
            if (dependents.length === 0) {
                return
            }
            const resolvedDependents = dependents.map(item => ({
                item,
                correspondingDependsOn: resolveCorrespondingDependsOn(item)
            }))
            const missingCorrespondingDependsOn = resolvedDependents.find(
                ({correspondingDependsOn}) => !correspondingDependsOn.item
            )
            if (missingCorrespondingDependsOn) {
                return [
                    createMissingCorrespondingDependsOnError(
                        missingCorrespondingDependsOn.item,
                        missingCorrespondingDependsOn.correspondingDependsOn.pointer
                    )
                ]
            }
            const firstFiveDependents = JSON.stringify(
                resolvedDependents.slice(0, 5).map(({item, correspondingDependsOn}) => {
                    const dependsOnItem = correspondingDependsOn.item!
                    return {
                        id: dependsOnItem.from.id,
                        type: dependsOnItem.from.namespace,
                        fragmentType: item.to.fragmentType,
                        fragmentInstance: item.to.fragmentInstance
                    }
                })
            )
            return [
                {
                    shouldFail: true,
                    type: 'existingDependentsError',
                    message: `Cannot delete ${JSON.stringify(
                        pointer.fragmentType ? pointer : {...pointer, fragmentType}
                    )} because it has the following dependents: ${firstFiveDependents}`,
                    extras: {deletedPointer: pointer, dependentCount: dependents.length}
                }
            ]
        }

        return {
            validateDependsOnBidirectional,
            validateDependedByBidirectional,
            validateDependsOnConfig,
            validateDependsOnReferenceExists,
            validateDependedByReferenceExists,
            validateNoDependentsOnDelete
        }
    }

    return {
        name: 'fragmentDependencies',
        dependencies: new Set(['fragments', 'dataModel']),
        getDocumentDataTypes,
        createFilters,
        createExtensionAPI,
        createValidator,
        validatorScope: {
            include: ['*'],
            exclude: []
        }
    }
}

export {createExtension}
